import type { FC } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import styled from "styled-components";
import type { GroupCount } from "../../../shared/issuance_utils.ts";

export interface IssuancePolicyChartProps {
  data: GroupCount[];
  onSegmentClick?: (policyId: string) => void;
}

const ChartWrapper = styled.div`
  width: 100%;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyState = styled.p`
  font-family: var(--vmx-font);
  font-size: 0.875rem;
  color: var(--vmx-gray-500);
  text-align: center;
`;

const COLORS = [
  "#7b2d8e",
  "#a855f7",
  "#d946ef",
  "#e879a8",
  "#f0abfc",
  "#c084fc",
  "#9333ea",
  "#6b21a8",
];

interface CustomTooltipProps {
  active?: boolean;
  payload?: { payload: GroupCount }[];
  total: number;
}

const CustomTooltip: FC<CustomTooltipProps> = ({ active, payload, total }) => {
  const first = active ? payload?.[0] : undefined;
  if (!first) {
    return null;
  }
  const entry = first.payload;
  const percentage = total > 0 ? ((entry.count / total) * 100).toFixed(1) : "0";
  return (
    <div
      style={{
        background: "white",
        border: "1px solid #ccc",
        padding: "8px 12px",
        borderRadius: "4px",
        fontSize: "0.875rem",
      }}
    >
      <p style={{ margin: 0, fontWeight: 600 }}>{entry.name}</p>
      <p style={{ margin: 0 }}>
        {entry.count} certificate{entry.count !== 1 ? "s" : ""} ({percentage}%)
      </p>
    </div>
  );
};

export const IssuancePolicyChart: FC<IssuancePolicyChartProps> = ({
  data,
  onSegmentClick,
}) => {
  if (data.length === 0) {
    return (
      <ChartWrapper>
        <EmptyState>No certificates issued in this period</EmptyState>
      </ChartWrapper>
    );
  }

  const total = data.reduce((sum, entry) => sum + entry.count, 0);

  return (
    <ChartWrapper>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={data}
            dataKey="count"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={100}
            label={({ name }) => name}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${String(index)}`}
                fill={COLORS[index % COLORS.length]}
                stroke="none"
                cursor={onSegmentClick ? "pointer" : undefined}
                onClick={() => onSegmentClick?.(entry.id)}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip total={total} />} />
        </PieChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
};
